<script setup>
import { cookieRef } from '@/@layouts/stores/config'
import { VDataTableServer } from 'vuetify/lib/labs/VDataTable/index.mjs'
import VisitingRuleDialog from '@/views/care-visit/visiting-rule/VisitingRuleDialog.vue'
import visitingRuleService from '@/services/care-visit/visiting-rule.service'

definePage({
  meta: {
    name: "Visiting Rule",
    subject: "care_visit.menu",
    action: "visiting_rule",
  },
})

const unitId = computed(() => cookieRef('selectedUnit').value ?? null)

const headers = ref([
  {
    title: 'Location',
    key: 'visitor_location.name',
  },
  {
    title: 'Number Of Visitors',
    key: 'number_of_visitors',
  },
  {
    title: 'Visiting Time',
    key: 'visiting_time',
  },
  {
    title: 'Number Of Bystanders',
    key: 'number_of_bystanders',
  },
  {
    title: 'Status',
    key: 'status',
  },
  {
    title: 'Actions',
    key: 'actions',
  },
])

const filter = computed(() => {
  return {
    unit_id: unitId.value,
  }
})

const searchQuery = ref('')
const itemsPerPage = ref(10)
const pageNumber = ref(1)
const sortBy = ref()
const orderBy = ref()

const {
  isFetching,
  data: visitingRulesData,
  execute: fetchVisitingRules,
} = useApi(createUrl(`/care-visit/visiting-rules`, {
  query: {
    paged: 1,
    q: searchQuery,
    search: filter,
    itemsPerPage: itemsPerPage,
    page: pageNumber,
    sort: sortBy,
    order_by: orderBy,
  },
}), {
  afterFetch: ({ data }) => {
    //
  },
})

const visitingRules = computed(() => visitingRulesData.value?.data ?? visitingRulesData.value)
const totalVisitingRules = computed(() => visitingRulesData.value?.meta?.total ?? visitingRulesData.value?.length)

const isVisitingRuleDialogOpen = ref(false)
const visitingRule = ref()

const editVisitorRule = item => {
  visitingRule.value = item
  isVisitingRuleDialogOpen.value = true
}

watch(() => isVisitingRuleDialogOpen.value, value => {
  if (!value) visitingRule.value = null
})

const deleteVisitorRule = item => {
  if (confirm('Are you sure you want to delete this visiting rule?')) {
    visitingRuleService.destroy(
      item.id,
      () => {
        fetchVisitingRules()
      },
      () => {
        //
      },
    )
  }
}
</script>

<template>
  <VCard
    variant="flat"
    class="border"
  >
    <VCardText class="d-flex flex-wrap py-4 gap-4">
      <div
        class="me-3 d-flex gap-3"
        style="inline-size: 24rem;"
      >
        <!-- -->
      </div>
      <VSpacer />

      <div class="app-user-search-filter d-flex align-center flex-wrap gap-4">
        <!-- 👉 Search  -->
        <div style="inline-size: 12rem;">
          <AppTextField
            v-model="searchQuery"
            placeholder="Search"
            density="compact"
          />
        </div>
        <VBtn
          variant="flat"
          prepend-icon="tabler-plus"
          :disabled="!$can('create', 'care_visit.visiting_rule')"
          @click="isVisitingRuleDialogOpen = true"
        >
          Create Rule
        </VBtn>
        <IconBtn
          variant="tonal"
          class="rounded"
          :disabled="isFetching"
          @click="fetchVisitingRules"
        >
          <VIcon icon="tabler-refresh" />
        </IconBtn>
      </div>
    </VCardText>

    <VDivider />

    <VDataTableServer
      v-model:items-per-page="itemsPerPage"
      v-model:page="pageNumber"
      :items-length="totalVisitingRules"
      :items="visitingRules ?? []"
      :headers="headers"
      fixed-header
      class="text-no-wrap"
      :loading="isFetching"
      @click:row="handleRowClick"
    >
      <template #item.number_of_visitors="{ item }">
        {{ item?.number_of_visitors ? `${item?.number_of_visitors} Person` : 'N/A' }}
      </template>
      <template #item.visiting_time="{ item }">
        {{ `${item.visiting_start_time} - ${item.visiting_end_time}` }}
      </template>
      <template #item.number_of_bystanders="{ item }">
        {{ item?.number_of_bystanders ? `${item?.number_of_bystanders} Person` : 'N/A' }}
      </template>
      <template #item.status="{ item }">
        <VChip
          v-if="item?.status"
          color="success"
        >
          Active
        </VChip>
        <VChip v-else>
          Inactive
        </VChip>
      </template>
      <template #item.actions="{ item }">
        <IconBtn
          :disabled="!$can('update', 'care_visit.visiting_rule')"
          @click="editVisitorRule(item)"
        >
          <VIcon icon="tabler-edit" />
        </IconBtn>
        <IconBtn
          :disabled="!$can('delete', 'care_visit.visiting_rule')"
          @click="deleteVisitorRule(item)"
        >
          <VIcon icon="tabler-trash" />
        </IconBtn>
      </template>
    </VDataTableServer>
  </VCard>

  <VisitingRuleDialog
    v-model:isDialogOpen="isVisitingRuleDialogOpen"
    :form-data="visitingRule"
    @saved="fetchVisitingRules"
  />
</template>
